package com.islamicmedia.site

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.snapshots.Snapshot
import com.islamicmedia.shared.AppConfig
import com.islamicmedia.shared.Environment
import com.islamicmedia.shared.data.storage.SettingsStorage
import com.islamicmedia.shared.util.ReferralCodeFormat
import com.varabyte.kobweb.core.App
import com.varabyte.kobweb.silk.SilkApp
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.url.URLSearchParams
import kotlin.js.Date

// Apply the dev-only backend override at module load time, before any page composable runs and
// before anything touches SupabaseAuthService.instance (which lazy-init reads AppConfig URLs).
// Module-level init runs once per page load — across all Kobweb routes — so the override is
// applied uniformly on / and /admin and any other page.
private val applyEnvOverride: Unit = run {
    if (AppConfig.isDev) {
        try {
            val path = window.location.pathname
            val isAdminConsole = path == "/admin" || path.startsWith("/admin/") || path == "/admin.html"
            AppConfig.runtimeOverride = if (isAdminConsole) {
                // The admin console is hosted on DEV but administers PRODUCTION — dev has no real
                // users, so a dev-pointed console would be empty and useless. Pinned here, at module
                // load before SupabaseAuthService lazy-inits, so the PROD Supabase project is also
                // what sign-in goes through: the server validates a token against its OWN project,
                // so a DEV session would be rejected by the PROD /api/admin/* endpoints.
                //
                // Deliberately IGNORES localStorage: the dev site has a public one-click
                // "Switch to PROD" banner that writes that key origin-wide, and the admin console
                // must not be steerable — in either direction — by a toggle any visitor can flip.
                Environment.PROD
            } else {
                when (window.localStorage.getItem("env-override")) {
                    "PROD" -> Environment.PROD
                    "DEV" -> Environment.DEV
                    else -> null
                }
            }
        } catch (_: Throwable) { /* SSR / no-window — ignore */ }
    }
}

// Capture where this visitor came from, at module load, before ANY page composable runs.
//
// It has to be here rather than in a page effect for two reasons:
//  1. Index.kt's startup effect calls history.replaceState(..., location.pathname) whenever ?q /
//     ?auth / ?launch is present, which destroys the whole query string — utm params included —
//     before a sibling effect could read them. Newsletter links use ?launch=1, so that is not a
//     corner case, it is the main email path.
//  2. Module init runs once per page load on EVERY Kobweb route, so an /isitsafe/* landing is
//     attributed too. Those pages never mount Index() and would otherwise be invisible.
//
// First-touch is enforced inside Funnel.recordFirstTouch, so running this on every page load is
// safe: only the first one that carries a real source ever gets stored.
private val captureAttribution: Unit = run {
    try {
        com.islamicmedia.shared.platform.Funnel.recordFirstTouch(
            com.islamicmedia.shared.platform.Attribution.from(
                rawQuery = window.location.search,
                referrerUrl = document.referrer,
                landingPath = window.location.pathname,
            )
        )
    } catch (_: Throwable) { /* SSR / no-window / blocked storage — attribution is never critical */ }
}

// A friend's referral code from their share link: /r/CODE redirects to /register?refcode=CODE.
// Saved to localStorage (SettingsStorage's pending referral code, good for 30 days) for the two
// places that use it: /register sends it with the checkout, and the app attaches it to the account
// after sign-in (MediaCheckerViewModel.flushPendingReferralCode).
//
// Here at module init for the same two reasons as captureAttribution above: Index.kt's
// replaceState strips the query string, and /register never mounts Index at all.
//
// The param is `refcode`, NEVER `ref`: Attribution.from already reads `ref` as a forwarded
// referrer host. A value that isn't a code is ignored rather than saved, so a mangled link can't
// wipe a good code from an earlier one; a newer good code replaces an older one (last link wins).
private val captureReferral: Unit = run {
    try {
        URLSearchParams(window.location.search).get("refcode")
            ?.let(ReferralCodeFormat::normalize)
            ?.let { SettingsStorage().savePendingReferralCode(it, Date.now().toLong()) }
    } catch (_: Throwable) { /* SSR / no-window / blocked storage — a lost code only costs the free month */ }
}

@App
@Composable
fun AppEntry(content: @Composable () -> Unit) {
    // Touch the fields so the module-init runs (Kotlin/JS may otherwise tree-shake them).
    applyEnvOverride
    captureAttribution
    captureReferral

    // WEB-ONLY recomposition fix (mobile is unaffected — this file is jsMain only).
    //
    // On Compose HTML the recomposer only recomposes changes it has been told about via
    // Snapshot.sendApplyNotifications(). A snapshot-state write made from a BACKGROUND coroutine —
    // e.g. a StateFlow/ViewModel update applied when a network call resumes — is committed by
    // compose-html's built-in GlobalSnapshotManager, which schedules that call on a microtask guarded
    // by a flag it only clears AFTER running the observers. If an apply ever throws (or the microtask
    // races), that flag stays set and NO further global write is ever flushed — the UI silently
    // freezes on the last state and only "unsticks" when some unrelated event forces a recomposition.
    // That is what made web disambiguation appear to hang forever: the /api/disambiguate result landed
    // in uiState within ~3s but "Finding matches…" never advanced until the user happened to interact.
    // Mobile never hit this because the Android/iOS runtimes flush apply-notifications every frame.
    //
    // Fix: register our OWN global write observer that schedules a flush on the next animation frame,
    // with a flag we clear BEFORE flushing — so a throwing/racy apply can never wedge it permanently.
    // It does real work only when snapshot state actually changes.
    LaunchedEffect(Unit) {
        var scheduled = false
        Snapshot.registerGlobalWriteObserver {
            if (!scheduled) {
                scheduled = true
                window.requestAnimationFrame {
                    scheduled = false
                    Snapshot.sendApplyNotifications()
                }
            }
        }
    }

    // Load Arabic font stylesheet and Playfair Display
    LaunchedEffect(Unit) {
        val link = document.createElement("link")
        link.setAttribute("rel", "stylesheet")
        link.setAttribute("href", "/fonts/arabic-font.css")
        document.head?.appendChild(link)

        val gfonts = document.createElement("link")
        gfonts.setAttribute("rel", "stylesheet")
        gfonts.setAttribute("href", "https://fonts.googleapis.com/css2?family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap")
        document.head?.appendChild(gfonts)
    }

    SilkApp {
        Div(attrs = {
            style {
                width(100.percent)
                // dvh (not vh) so mobile browsers size to the VISIBLE viewport (excludes the URL
                // bar) — otherwise 100vh over-sizes the page and pushes the footer below the fold.
                property("min-height", "100dvh")
                property("font-family", "'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif")
            }
        }) {
            content()
        }
    }
}
